import { useEffect, useState } from 'react';

/** A tiny history router: '/', '/play' (vs computer), '/learn' (the tutorial), '/r/CODE' (online room). */
export type Route = { name: 'home' } | { name: 'solo' } | { name: 'learn' } | { name: 'room'; code: string };

export function parseRoute(pathname: string): Route {
  const m = /^\/r\/([A-Za-z]{4})\/?$/.exec(pathname);
  if (m) return { name: 'room', code: m[1]!.toUpperCase() };
  if (/^\/play\/?$/.test(pathname)) return { name: 'solo' };
  if (/^\/learn\/?$/.test(pathname)) return { name: 'learn' };
  return { name: 'home' };
}

export function pathFor(route: Route): string {
  if (route.name === 'room') return `/r/${route.code}`;
  if (route.name === 'solo') return '/play';
  if (route.name === 'learn') return '/learn';
  return '/';
}

const listeners = new Set<() => void>();

export function navigate(route: Route, replace = false): void {
  const path = pathFor(route);
  if (location.pathname !== path) {
    if (replace) history.replaceState(null, '', path);
    else history.pushState(null, '', path);
  }
  listeners.forEach((fn) => fn());
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(location.pathname));
  useEffect(() => {
    const update = () => setRoute(parseRoute(location.pathname));
    listeners.add(update);
    window.addEventListener('popstate', update);
    return () => {
      listeners.delete(update);
      window.removeEventListener('popstate', update);
    };
  }, []);
  return route;
}

/** `?speed=0.2` scales every bot delay (testing). Clamped to 0.05..3. */
export function speedFromUrl(search = typeof location !== 'undefined' ? location.search : ''): number {
  const raw = new URLSearchParams(search).get('speed');
  const n = raw === null ? NaN : Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.min(3, Math.max(0.05, n)) : 1;
}
